/**
 * Report form (tutoring).
 */
import * as React from 'react';
import { Star, GraduationCap, UserCheck, Smile, FileText } from 'lucide-react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { CollapseReplaceForm } from '@/components/common/CollapseReplaceForm';
import { MultiSelect } from '@/components/forms/MultiSelect';
import { Rating } from '@/components/forms/Rating';
import { Textarea } from '@/components/forms/Textarea';
import { ParticipantChip } from '@/components/common/ParticipantChip';
import {
  getReport,
  updateReport,
  getStudentFeedbackCatalog,
} from '@/services/inperson/reports.service';
import { getSession } from '@/services/inperson/sessions.service';
import { getSubjects } from '@/services/subjects.service';
import { DetailsCard } from '@/components/common/DetailsCard';
import { TutorFeedbackReadOnly } from '@/components/reports/inperson/renderReportDetails';
import type {
  StudentFeedbackEdit,
  TutorFeedbackValue,
  UpdateReportDto,
} from '@/types/inperson/report.types';
import type { ParticipantAttendance } from '@/types/attendance.types';
import { useAuthStore } from '@/stores/authStore';
import { getRoleLabel } from '@/utils/role-label-utils';
import { useFormValidation } from '@/hooks/useFormValidation';
import { reportValidationSchema } from '@/utils/validation-schemas-utils';
import {
  buildTutorReportSections,
  isMultiAuthorReport,
  isPrivilegedReportEditor,
} from '@/utils/report-author-feedback-utils';
import type { SessionParticipant } from '@/types/inperson/session.types';
import {
  REPORT_IN_PERSON_QUERY_KEYS as QUERY_KEYS,
  REPORT_IN_PERSON_SUBJECTS_QUERY as SUBJECTS_QUERY,
  SESSION_REPORT_FEELING_OPTIONS,
} from '@/components/reports/report-configs';
import { AttendanceSection } from './AttendanceSection';
import {
  buildUpdateTutoringReportDto,
  collectAttendanceErrors,
  hydrateTutoringReportEditState,
  initAttendanceMap,
  isAttendanceMapValid,
  resolveEditableAuthorIds,
  resolveReportSubjectOptions,
  resolveTutoringReportSubjectPrefill,
  setAttendanceEntry,
  getEditableAttendanceParticipants,
  getStudentParticipants,
  mapStudentFeedbackCatalogOptions,
  partitionParticipantsForReport,
  subjectValueForEditValidation,
} from '@/components/reports/inperson/report-form-utils';
import { toast } from '@/components/ui/toast';
import { getErrorMessage } from '@/utils/errors-utils';

/** Volunteer chips on the tutoring report (presence not collected here). */
function VscSessionParticipantsBlock({
  participants,
}: Readonly<{ participants: SessionParticipant[] }>) {
  if (participants.length === 0) return null;

  const caption =
    participants.length === 1
      ? "Ce VSC participe à la séance. Sa présence n'est pas saisie dans le compte rendu."
      : "Ces VSC participent à la séance. Leur présence n'est pas saisie dans le compte rendu.";

  return (
    <div className="form-section">
      <div className="form-section-title">
        <UserCheck className="w-4 h-4" />
        <span>VSC présents sur la séance</span>
        <span
          className="text-xs font-bold px-2 py-0.5 rounded-full border"
          style={{
            background: 'color-mix(in srgb, var(--color-3-nuance-1) 12%, transparent)',
            color: 'var(--color-3-nuance-1)',
            borderColor: 'var(--color-3-nuance-1)',
          }}
        >
          {participants.length}
        </span>
      </div>
      <div className="rounded-lg border border-[color-mix(in_srgb,var(--color-3-nuance-1)_35%,transparent)] bg-[color-mix(in_srgb,var(--color-3-nuance-1)_6%,transparent)] p-4">
        <p className="text-sm text-muted-foreground mb-3">{caption}</p>
        <ul className="flex flex-wrap gap-2 list-none p-0 m-0">
          {participants.map((p) => (
            <li key={p.id}>
              <ParticipantChip
                user={p.user}
                role="volunteer"
                volunteerRoleLabel={getRoleLabel('volunteer')}
                size="md"
                showTooltip
              />
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/** Visual validation state for a tutor feedback field. */
type FeedbackFieldState = 'default' | 'error' | 'success';

/** Tutor rating and comment fields with validation state. */
function TutorFeedbackFields({
  value,
  onChange,
  ratingState,
  ratingError,
  commentState,
  commentError,
  onBlurRating,
  onBlurComment,
}: Readonly<{
  value: TutorFeedbackValue;
  onChange: (next: TutorFeedbackValue) => void;
  ratingState?: FeedbackFieldState;
  ratingError?: string;
  commentState?: FeedbackFieldState;
  commentError?: string;
  onBlurRating?: () => void;
  onBlurComment?: () => void;
}>) {
  return (
    <div className="space-y-4">
      <Rating
        label="Note de la séance"
        required
        value={value.rating}
        onChange={(rating) => onChange({ ...value, rating: rating ?? null })}
        onBlur={onBlurRating}
        state={ratingState}
        error={ratingError}
        helperText="Évaluez la qualité de la séance"
      />

      <div>
        <h5 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
          <Smile className="w-4 h-4" />
          Comment vous êtes-vous senti pendant cette séance ?
          <span className="text-destructive">*</span>
        </h5>
        <div className="flex flex-col gap-2">
          {SESSION_REPORT_FEELING_OPTIONS.map(({ value: optValue, label }) => {
            const selected = value.feeling === optValue;
            return (
              <button
                key={optValue}
                type="button"
                onClick={() =>
                  onChange({
                    ...value,
                    feeling: selected ? null : optValue,
                  })
                }
                className={`rounded-lg border px-3 py-2.5 text-left text-sm transition-colors ${
                  selected
                    ? 'border-[var(--color-2-nuance-1)] bg-[color-mix(in_srgb,var(--color-2-nuance-1)_12%,transparent)] font-medium text-foreground'
                    : 'border-border bg-background text-foreground hover:bg-muted/50'
                }`}
              >
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <div>
        <h5 className="text-sm font-medium text-foreground mb-2 flex items-center gap-2">
          <FileText className="w-4 h-4" />
          Commentaire
        </h5>
        <Textarea
          label="Commentaires et observations"
          required
          placeholder="Ex: Bonne participation, progrès notables"
          value={value.comment}
          maxLength={1000}
          showCharCount
          onChange={(e) => onChange({ ...value, comment: e.target.value })}
          onBlur={onBlurComment}
          state={commentState}
          error={commentError}
          rows={4}
          autoResize
          helperText="Décrivez les observations et le déroulement de la séance"
        />
      </div>
    </div>
  );
}

/** Props for {@link TutoringReportForm}. */
export interface TutoringReportFormProps {
  sessionId: string;
  reportId: string;
  onClose: () => void;
  onSuccess?: () => void;
}

/** Edit form for an in-person tutoring report. */
export function TutoringReportForm({
  sessionId,
  reportId,
  onClose,
  onSuccess,
}: Readonly<TutoringReportFormProps>) {
  const user = useAuthStore((s) => s.user);
  const roles = useAuthStore((s) => s.roles);
  const privileged = isPrivilegedReportEditor(roles.map((r) => r.code));

  const [subjectIds, setSubjectIds] = React.useState<string[]>([]);
  const subjectIdsTouchedByUser = React.useRef(false);
  const [feedbackByAuthor, setFeedbackByAuthor] = React.useState<Map<string, TutorFeedbackValue>>(
    () => new Map(),
  );
  const [attendanceData, setAttendanceData] = React.useState<Map<string, ParticipantAttendance>>(
    () => new Map(),
  );
  const [attendanceErrors, setAttendanceErrors] = React.useState<Map<string, string>>(
    () => new Map(),
  );
  const [studentFeedbackData, setStudentFeedbackData] = React.useState<
    Map<string, StudentFeedbackEdit>
  >(() => new Map());

  const validation = useFormValidation(reportValidationSchema);

  const { data: report, isLoading } = useQuery({
    queryKey: QUERY_KEYS.report(sessionId, reportId),
    queryFn: () => getReport(sessionId, reportId),
  });

  const { data: session } = useQuery({
    queryKey: QUERY_KEYS.session(sessionId),
    queryFn: () => getSession(sessionId),
    enabled: !!sessionId,
  });

  const { data: allSubjects } = useQuery({
    queryKey: QUERY_KEYS.subjects,
    queryFn: () => getSubjects({ ...SUBJECTS_QUERY }),
    select: (response) => response.data,
  });

  const { data: studentFeedbackCatalog } = useQuery({
    queryKey: QUERY_KEYS.studentFeedbackCatalog,
    queryFn: getStudentFeedbackCatalog,
  });

  const multiAuthor = isMultiAuthorReport(session, report ?? { authorFeedbacks: [], authorId: '' });
  const sections = buildTutorReportSections(
    session,
    report ?? { authorFeedbacks: [], authorId: '' },
  );
  const editableAuthorIds = React.useMemo(
    () =>
      resolveEditableAuthorIds(
        !!report,
        user?.id,
        privileged,
        sections.map((section) => section.authorUserId),
      ),
    [report, user?.id, privileged, sections],
  );

  React.useEffect(() => {
    if (!report) return;
    subjectIdsTouchedByUser.current = false;
    const hydrated = hydrateTutoringReportEditState(report, user?.id);
    setFeedbackByAuthor(hydrated.feedbackByAuthor);
    setSubjectIds(hydrated.subjectIds);
    validation.validateInitialFormData(hydrated.initialValidation);
  }, [report, user?.id]);

  React.useEffect(() => {
    if (!report) return;
    setSubjectIds((prev) => {
      const next = resolveTutoringReportSubjectPrefill(
        report,
        session?.subjectIds,
        prev,
        subjectIdsTouchedByUser.current,
      );
      return next ?? prev;
    });
  }, [report, session]);

  React.useEffect(() => {
    const subj = subjectValueForEditValidation(subjectIds);
    const actor =
      (user?.id ? feedbackByAuthor.get(user.id) : undefined) ?? [...feedbackByAuthor.values()][0];
    validation.validateField('subject', subj, {
      rating: actor?.rating ?? null,
      comment: actor?.comment ?? '',
      subject: subj,
    } as never);
  }, [subjectIds]);

  const { vscParticipants, attendanceParticipants } = React.useMemo(
    () => partitionParticipantsForReport(session?.participants, session?.studentTutorPairs),
    [session?.participants, session?.studentTutorPairs],
  );

  const editableAttendanceParticipants = React.useMemo(
    () => getEditableAttendanceParticipants(attendanceParticipants),
    [attendanceParticipants],
  );

  const studentParticipants = React.useMemo(
    () => getStudentParticipants(editableAttendanceParticipants),
    [editableAttendanceParticipants],
  );

  const catalogOptions = React.useMemo(
    () => mapStudentFeedbackCatalogOptions(studentFeedbackCatalog),
    [studentFeedbackCatalog],
  );

  React.useEffect(() => {
    if (!editableAttendanceParticipants.length) {
      setAttendanceData(new Map());
      setAttendanceErrors(new Map());
      return;
    }
    setAttendanceData(initAttendanceMap(editableAttendanceParticipants));
    setAttendanceErrors(new Map());
  }, [editableAttendanceParticipants]);

  React.useEffect(() => {
    if (!report || !studentParticipants.length) return;
    const hydrated = hydrateTutoringReportEditState(report, user?.id, studentParticipants);
    setStudentFeedbackData(hydrated.studentFeedbacks);
  }, [report, studentParticipants]);

  const updateMutation = useMutation({
    mutationFn: (dto: UpdateReportDto) => updateReport(sessionId, reportId, dto),
    onSuccess: () => {
      toast.success('Compte rendu modifié avec succès !');
      onSuccess?.();
      onClose();
    },
    onError: (error: unknown) => {
      toast.error(getErrorMessage(error));
    },
  });

  const subjectForValidation = React.useCallback(
    (): string => subjectValueForEditValidation(subjectIds),
    [subjectIds],
  );

  const getActorFeedback = (): TutorFeedbackValue | undefined => {
    if (!user?.id) return undefined;
    return feedbackByAuthor.get(user.id) ?? feedbackByAuthor.values().next().value;
  };

  const handleFeedbackChange = (authorUserId: string, next: TutorFeedbackValue) => {
    setFeedbackByAuthor((prev) => {
      const nextMap = new Map(prev);
      nextMap.set(authorUserId, next);
      return nextMap;
    });
    if (editableAuthorIds.has(authorUserId)) {
      const subj = subjectForValidation();
      const ctx = {
        rating: next.rating,
        subject: subj,
        comment: next.comment,
        feeling: next.feeling,
      } as never;
      validation.validateField('rating', next.rating, ctx);
      validation.validateField('comment', next.comment, ctx);
      validation.validateField('feeling', next.feeling, ctx);
      validation.markAsTouched('rating');
      validation.markAsDirty('rating');
      validation.markAsTouched('feeling');
    }
  };

  const handleSubjectIdsChange = (values: string[]) => {
    subjectIdsTouchedByUser.current = true;
    setSubjectIds(values);
    const subj = subjectValueForEditValidation(values);
    const actor = getActorFeedback();
    validation.validateField('subject', subj, {
      rating: actor?.rating,
      comment: actor?.comment,
      subject: subj,
    } as never);
    validation.markAsTouched('subject');
    validation.markAsDirty('subject');
  };

  const handleBlur = (field: string) => {
    validation.markAsTouched(field);
  };

  const isAttendanceValid = React.useMemo(
    () => isAttendanceMapValid(attendanceData, editableAttendanceParticipants.length),
    [attendanceData, editableAttendanceParticipants],
  );

  const canEditAllAuthors = privileged && sections.length > 1;

  /** Validate, then PATCH the tutoring report (plus per-student feedback). */
  const handleSubmit = () => {
    const actor = getActorFeedback();
    const isValid = validation.validateForm({
      subject: subjectForValidation(),
      rating: actor?.rating,
      comment: actor?.comment ?? '',
      feeling: actor?.feeling ?? null,
    });
    if (!isValid) {
      validation.markAsTouched('subject');
      validation.markAsTouched('rating');
      validation.markAsTouched('comment');
      validation.markAsTouched('feeling');
      toast.error(getErrorMessage('requiredReportFields'));
      return;
    }

    if (editableAttendanceParticipants.length > 0) {
      const errors = collectAttendanceErrors(attendanceData);
      if (errors.size > 0) {
        setAttendanceErrors(errors);
        toast.error(getErrorMessage('requiredAttendanceReasons'));
        return;
      }
    }

    updateMutation.mutate(
      buildUpdateTutoringReportDto({
        feedbackByAuthor,
        canEditAllAuthors,
        editableAuthorIds,
        subjectIds,
        attendanceParticipants: editableAttendanceParticipants,
        attendanceData,
        studentParticipants,
        studentFeedbackData,
      }),
    );
  };

  const subjectOptions = React.useMemo(
    () => resolveReportSubjectOptions(session?.subjectIds, allSubjects ?? []),
    [session?.subjectIds, allSubjects],
  );

  const singleSection = !multiAuthor && sections.length === 1 ? sections[0] : null;
  const singleFeedback = singleSection
    ? feedbackByAuthor.get(singleSection.authorUserId)
    : getActorFeedback();

  const handleAttendanceChange = (id: string, next: ParticipantAttendance) => {
    setAttendanceData((prev) => setAttendanceEntry(prev, id, next));
  };

  return (
    <CollapseReplaceForm
      formType="edit"
      title="Modifier le compte rendu de tutorat"
      subtitle="Modifiez les informations du compte rendu de tutorat"
      onClose={onClose}
      onSubmit={handleSubmit}
      submitLabel="Enregistrer"
      isSubmitting={updateMutation.isPending}
      isValid={validation.isFormValid() && isAttendanceValid}
      isLoading={isLoading}
    >
      <div className="form-section">
        <h4 className="form-section-title">
          <Star className="w-4 h-4" />
          Matières
        </h4>
        <MultiSelect
          label="Matières abordées"
          required
          placeholder="Sélectionner une ou plusieurs matières"
          options={subjectOptions}
          value={subjectIds}
          onChange={handleSubjectIdsChange}
          onBlur={() => handleBlur('subject')}
          state={validation.getFieldState('subject')}
          error={validation.getFieldError('subject')}
          helperText="Matières abordées durant la séance"
          maxVisibleTags={3}
        />
      </div>

      {multiAuthor &&
        sections.map((section) => {
          const value = feedbackByAuthor.get(section.authorUserId) ?? {
            rating: null,
            comment: '',
            feeling: null,
          };
          const canEdit = editableAuthorIds.has(section.authorUserId);
          const roleLabel = getRoleLabel(section.role === 'volunteer' ? 'volunteer' : 'tutor');
          return (
            <DetailsCard
              key={section.authorUserId}
              icon={GraduationCap}
              title={`${roleLabel} — ${section.displayName}`}
              colorName="color-2"
              colSpan="full"
            >
              {canEdit ? (
                <TutorFeedbackFields
                  value={value}
                  onChange={(next) => handleFeedbackChange(section.authorUserId, next)}
                  ratingState={validation.getFieldState('rating')}
                  ratingError={validation.getFieldError('rating')}
                  commentState={validation.getFieldState('comment')}
                  commentError={validation.getFieldError('comment')}
                  onBlurRating={() => handleBlur('rating')}
                  onBlurComment={() => handleBlur('comment')}
                />
              ) : (
                <TutorFeedbackReadOnly
                  feedback={{
                    idAuteur: section.authorUserId,
                    rating: value.rating,
                    comment: value.comment || null,
                    feeling: value.feeling,
                  }}
                />
              )}
            </DetailsCard>
          );
        })}

      {multiAuthor && (vscParticipants.length > 0 || attendanceParticipants.length > 0) && (
        <>
          <VscSessionParticipantsBlock participants={vscParticipants} />
          <AttendanceSection
            participants={attendanceParticipants}
            attendanceData={attendanceData}
            errors={attendanceErrors}
            onChange={handleAttendanceChange}
          />
        </>
      )}

      {!multiAuthor && singleSection && singleFeedback && (
        <>
          <div className="form-section">
            <TutorFeedbackFields
              value={singleFeedback}
              onChange={(next) => handleFeedbackChange(singleSection.authorUserId, next)}
              ratingState={validation.getFieldState('rating')}
              ratingError={validation.getFieldError('rating')}
              commentState={validation.getFieldState('comment')}
              commentError={validation.getFieldError('comment')}
              onBlurRating={() => handleBlur('rating')}
              onBlurComment={() => handleBlur('comment')}
            />
          </div>
          <VscSessionParticipantsBlock participants={vscParticipants} />
          <AttendanceSection
            participants={attendanceParticipants}
            attendanceData={attendanceData}
            errors={attendanceErrors}
            onChange={handleAttendanceChange}
          />
        </>
      )}

      {multiAuthor &&
        !canEditAllAuthors &&
        sections.some((s) => !editableAuthorIds.has(s.authorUserId)) && (
          <p className="text-sm text-muted-foreground px-1">
            Les évaluations des autres tuteurs sont en lecture seule. Seule votre section est
            modifiable.
          </p>
        )}

      {studentParticipants.length > 0 && (
        <div className="form-section">
          <h4 className="form-section-title">
            <GraduationCap className="w-4 h-4" />
            Suivi pédagogique par élève
          </h4>
          <div className="space-y-6">
            {studentParticipants.map((participant) => {
              const fb = studentFeedbackData.get(participant.userId) ?? {
                workedItemIds: [],
                rating: null,
                comment: '',
              };
              const displayName =
                participant.user?.displayName ||
                [participant.user?.firstName, participant.user?.lastName]
                  .filter(Boolean)
                  .join(' ') ||
                'élève';

              const updateFb = (patch: Partial<StudentFeedbackEdit>) => {
                setStudentFeedbackData((prev) => {
                  const nextMap = new Map(prev);
                  nextMap.set(participant.userId, { ...fb, ...patch });
                  return nextMap;
                });
              };

              return (
                <div
                  key={participant.id}
                  className="rounded-lg border border-border bg-muted/25 p-4 space-y-4"
                >
                  <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                    <GraduationCap className="w-3.5 h-3.5 text-muted-foreground" />
                    {displayName}
                  </p>

                  <MultiSelect
                    label="Devoirs / matières travaillés"
                    placeholder="Sélectionner des matières ou activités"
                    options={catalogOptions}
                    value={fb.workedItemIds}
                    onChange={(ids) => updateFb({ workedItemIds: ids })}
                    maxVisibleTags={3}
                    showSelectAll={false}
                    helperText="Matières, devoirs ou activités travaillés avec cet élève"
                  />

                  <Rating
                    label="Note individuelle"
                    value={fb.rating}
                    onChange={(v) => updateFb({ rating: v ?? null })}
                    helperText="Évaluation de l'engagement / progression de l'élève (optionnel)"
                  />

                  <Textarea
                    label="Commentaire individuel"
                    placeholder="Observations sur cet élève…"
                    value={fb.comment}
                    onChange={(e) => updateFb({ comment: e.target.value })}
                    rows={3}
                    autoResize
                    maxLength={5000}
                    showCharCount
                    helperText="Commentaire libre associé à cet élève pour cette séance (optionnel)"
                  />
                </div>
              );
            })}
          </div>
        </div>
      )}
    </CollapseReplaceForm>
  );
}
